import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { canv } from "@/canv/global";
import { getBoolProperty, getNumberProperty, getStringProperty, isBrowser } from "@/canv/utils";
import { getViewport } from "@/canvasController/helpers";
import { ElementBase } from "@/elementBase";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import { EMBED_CONTAINER } from "./constants";
import { EmbedIframe } from "./EmbedIframe";
import { checkLinkForEmbed, linkToEdit, replaceLink } from "./utils";

let DRAW_ERROR_LOGGED = false;

export class EmbedWidget extends ElementBase {
    constructor(src?: string, width = 400, height = 300) {
        super();

        this.src = src || null;
        this.width = width;
        this.height = height;
        this.clipWidthShare = 1;
        this.clipHeightShare = 1;
        this.clipDxShare = 0;
        this.clipDyShare = 0;

        if (src !== undefined) {
            this.initIframe();
        }

        this.updateDims();
    }

    updateDims() {
        if (!isBrowser()) return;
        this.updateBase(
            this.startX + this.clipDxShare * this.width,
            this.startY + this.clipDyShare * this.height,
            this.width * this.clipWidthShare,
            this.height * this.clipHeightShare,
        );
    }

    shouldDraw() {
        if (!this.container) {
            return;
        }
        const isShouldDraw = this.parent?.isGroupOnScreen();

        if (!isShouldDraw) {
            this.container.style.display = "none";

            return false;
        }

        this.container.style.display = "block";

        return true;
    }

    clickOnBody = () => {
        this.unActiveFrame();
    };

    addClickOnBodyListener() {
        document.body.addEventListener("mousedown", this.clickOnBody);
    }

    removeClickOnBodyListener() {
        document.body.removeEventListener("mousedown", this.clickOnBody);
    }

    blurFrame() {
        if (!this.container) {
            return;
        }

        const iframe = this.container.querySelector("iframe");

        iframe?.blur();

        this.removeClickOnBodyListener();
    }

    loadIframe = () => {
        this.blurFrame();

        this.loading = false;
    };

    activeFrame() {
        const overlayFrame = this.container?.getElementsByClassName("overlayFrame")[0];

        const frame = this.container?.querySelector(".embedFrame") as HTMLIFrameElement;
        frame.src = linkToEdit(this.src as string);

        this.container?.classList.add("embedContainerActive");
        overlayFrame?.classList.remove("overlayFrameHover");

        this.addClickOnBodyListener();
    }

    unActiveFrame() {
        this.blurFrame();

        const frame = this.container?.querySelector(".embedFrame") as HTMLIFrameElement;
        frame.src = replaceLink(this.src as string) as string;

        this.container?.classList.remove("embedContainerActive");
    }

    isInteraction(context: string): { type: string } {
        if (context === "focus") {
            this.activeFrame();
            return { type: "interaction" };
        }

        return { type: "none" };
    }

    TryAction(type: string): void {
        if (type === "mousedown") {
            canv.toolBox.TryGetToolByName("cursorTool")?.turnMode("");

            this.unActiveFrame();
        }
    }

    initIframe() {
        this.loading = true;

        if (!checkLinkForEmbed(this.src || "")) return;

        this.container = document.createElement("div");
        this.container.classList.add(EMBED_CONTAINER);
        this.container.id = this.UID;
        this.src = replaceLink(this.src);

        if (this.isHidden) {
            this.container.style.visibility = "hidden";
        }

        this.translateContainer();

        if (!this.src) {
            throw new Error("Некорректный url");
        }

        const iframe = React.createElement(EmbedIframe, { src: this.src, onLoad: this.loadIframe });

        this.root = createRoot(this.container);
        this.root.render(iframe);

        document.body.appendChild(this.container);

        this.blurFrame();
    }

    translateContainer() {
        if (this.container === null || canv.isHighPdfExporting) {
            return;
        }

        const viewport = getViewport();
        const zoom = document.body.clientWidth / viewport.viewportWidth;
        const width = 796;
        const height = 596;
        const scale = (this.width / width) * zoom;
        const x = (viewport.viewportStartX - this.startX) * zoom * -1;
        const y = (viewport.viewportStartY - this.startY) * zoom * -1;

        this.container.style.width = `${width}px`;
        this.container.style.height = `${height}px`;
        this.container.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    }

    checkIsHover() {
        if (this.container === null) {
            return;
        }

        const isActive = this.container?.classList.contains("embedContainerActive");
        const overlayFrame = this.container?.getElementsByClassName("overlayFrame")[0];

        if (this.isOver() && !isActive) {
            overlayFrame?.classList.add("overlayFrameHover");
        } else {
            overlayFrame?.classList.remove("overlayFrameHover");
        }
    }

    draw() {
        try {
            canv.ctx.save();
            this.checkIsHover();
            this.translateContainer();
            canv.ctx.fillStyle = "#fff";
            canv.ctx.fillRect(this.startX, this.startY, this.width, this.height);
            canv.ctx.restore();
        } catch (err) {
            console.error(err);
            if (!DRAW_ERROR_LOGGED) {
                DRAW_ERROR_LOGGED = true;
                canv.captureException(err);
            }
        }
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;

        this.updateDims();
    }

    resize(x: number, y: number) {
        this.unActiveFrame();

        const diffValue = (x + y) / 2;
        const hvRatio = this.height / this.width;

        this.height += hvRatio * diffValue;
        this.width += diffValue;
    }

    getWidgetOptions() {
        return [new widgetOption("linkTo", "", this.UID)];
    }

    getHandlerForOption(name: string) {
        if (name === "linkTo" && this.src) {
            window.open(linkToEdit(this.src));
        }
    }

    setHidden(value: boolean) {
        this.fixState("hide");
        this.isHidden = value;
        this.saveState("hide");

        if (value) {
            if (this.container) {
                this.container.style.visibility = "hidden";
            }
        } else {
            if (this.container) {
                this.container.style.visibility = "visible";
            }
        }
    }

    beforeDeletion() {
        this.container?.parentNode?.removeChild(this.container);
        this.root?.unmount();
    }

    getFixType(reason: string) {
        let fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;
            case "resize":
                fixType = "resize";
                break;
            case "hide":
                fixType = "hide";
                break;
        }

        return fixType;
    }

    getState(fixType: string) {
        switch (fixType) {
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                };
            case "resize":
                return {
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                };
            case "hide":
                return {
                    isHidden: this.isHidden,
                };
            case "full":
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "embedWidget",
                    src: this.src,
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                    clipDxShare: this.clipDxShare,
                    clipDyShare: this.clipDyShare,
                    clipWidthShare: this.clipWidthShare,
                    clipHeightShare: this.clipHeightShare,
                    miroX: 0,
                    miroY: 0,
                    miroWidth: 0,
                    isHidden: this.isHidden,
                };
            default:
                return {};
        }
    }

    loadState(stateObj: this, fixType: string) {
        switch (fixType) {
            case "move":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                break;
            case "resize":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                break;
            case "hide":
                this.isHidden = getBoolProperty("isHidden", stateObj);
                break;
            case "full":
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                this.src = stateObj.src;
                this.isHidden = getBoolProperty("isHidden", stateObj);

                this.initIframe();
                break;
        }

        this.updateDims();
    }

    constructorName: "embed" = "embed";
    src: string | null = null;

    width = 0;
    height = 0;
    startX = 0;
    startY = 0;
    clipDxShare = 0;
    clipDyShare = 0;
    clipWidthShare = 1;
    clipHeightShare = 1;

    container: HTMLElement | null = null;
    root: Root | null = null;
    loading = false;
    rotatable = false;
    isHidden = false;
}
